import { useQuery } from '@apollo/react-hooks';
import ColorThief from 'colorthief';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Fade,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {
  createMuiTheme,
  createStyles,
  makeStyles,
  Theme,
} from '@material-ui/core/styles';
import { ThemeProvider } from '@material-ui/styles';
import gql from 'graphql-tag';
import useCurrentUser from 'hooks/useCurrentUser';
import moment from 'moment';
import React, {
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ProgressBar } from 'react-native-paper';
import MaterialIcon from 'react-native-vector-icons/MaterialIcons';
import { useHistory } from 'react-router-dom';
import DateRangeModal, {
  customDateRangeDisplay,
  DateRange,
} from 'shared/components/DateRangeModal';
import ErgIcon from 'shared/components/ErgIcon';
import Logo from 'shared/components/Logo';
import { green, HRZones, red } from 'shared/components/metrics';
import { darkTheme } from 'shared/styles';
import { ErgType } from 'shared/types';
import {
  calculatePace,
  calculatePower,
  calculateProgressFromPrevious,
  ergTypeColor,
  ergTypeDisplay,
  formatTime,
  humanizeDistance,
} from 'shared/utils';
import { muiThemeAttributes } from 'utils/helpers';
import { isMobileApp, postMobileMessage } from 'utils/mobile';
import { t } from 'shared/i18n';

const colorThief = new ColorThief();

const STATS_FRAGMENT = gql`
  fragment StatsFragment on UserStats {
    totalDistance
    totalDays
    totalTime
    totalCalories
    weightedDistance
    hrZones
    rates
  }
`;

const GET_STATS = gql`
  query GetStats(
    $time: String!
    $startDate: String
    $endDate: String
    $timezone: String
    $ergTypes: [String]
    $skipPreviousStats: Boolean!
  ) {
    userStats: myStats(
      time: $time
      ergTypes: $ergTypes
      startDate: $startDate
      endDate: $endDate
      timezone: $timezone
    ) {
      ...StatsFragment
      recentStats {
        name
        image
        total
      }
      distanceOverTime {
        period
        label
        total
        byErg {
          ergType
          total
        }
      }
      distanceGroupBy
    }
    previousStats: myStats(
      time: $time
      previousPeriod: true
      ergTypes: $ergTypes
      startDate: $startDate
      endDate: $endDate
      timezone: $timezone
    ) @skip(if: $skipPreviousStats) {
      ...StatsFragment
    }
  }
  ${STATS_FRAGMENT}
`;

interface UserStats {
  totalDistance: number;
  totalDays: number;
  totalTime: number;
  totalCalories: number;
  weightedDistance: number;
  hrZones: number[];
  rates: number[];
  recentStats: Stat[];
  distanceOverTime?: DistanceOverTimeStat[];
  distanceGroupBy?: string;
}

interface DistanceOverTimeErgStat {
  ergType: string;
  total: number;
}

interface DistanceOverTimeStat {
  period: string;
  label: string;
  total: number;
  byErg: DistanceOverTimeErgStat[];
}

interface Stat {
  name: string;
  total: number;
  image: string;
}

interface QueryData {
  userStats: UserStats;
  previousStats: UserStats;
}

const Row = forwardRef(
  ({ stat, percentage }: { stat: Stat; percentage: number }, ref) => {
    const [color, setColor] = useState('rgba(255,255,255, 0.2)');
    const imageRef = useRef<HTMLImageElement>(null);

    const onLoad = useCallback(() => {
      if (imageRef.current) {
        try {
          const newColor = colorThief.getPalette(imageRef.current)[0];
          setColor(`rgba(${newColor[0]}, ${newColor[1]}, ${newColor[2]}, 0.9)`);
        } catch (error) {
          // Fallback to default color if color extraction fails
          setColor('rgba(255,255,255, 0.2)');
        }
      }
    }, []);

    // @ts-ignore
    const styles = useStyles();
    return (
      <div
        className={styles.row}
        // @ts-ignore
        ref={ref}
      >
        <div
          className={styles.bar}
          style={{
            backgroundColor: color,
            width: `${75 * percentage}%`,
          }}
        >
          <img
            alt={`${stat.name} logo`}
            className={styles.image}
            src={stat.image}
            ref={imageRef}
            crossOrigin="anonymous"
            onLoad={onLoad}
          />
        </div>
        <div className={styles.rowMetricContainer}>
          <div className={styles.rowName}>{stat.name}</div>
          {humanizeDistance(stat.total)}
        </div>
      </div>
    );
  }
);

const CHART_PLOT_HEIGHT = 180;

const DistanceOverTimeChart = ({
  data,
  groupBy,
  ergTypes,
}: {
  data: DistanceOverTimeStat[];
  groupBy: string;
  ergTypes: ErgType[];
}) => {
  const styles = useStyles();
  const maxTotal = Math.max(...data.map((period) => period.total), 1);
  const activeErgs = ergTypes.filter((ergType) =>
    data.some((period) =>
      period.byErg.some((entry) => entry.ergType === ergType && entry.total > 0)
    )
  );
  const labelInterval = data.length > 12 ? Math.ceil(data.length / 12) : 1;
  const barGap = data.length > 60 ? 2 : data.length > 31 ? 4 : 6;
  const chartMinWidth = Math.max(data.length * 18, 280);
  const groupLabelMap = {
    day: t('Day'),
    week: t('Week'),
    month: t('Month'),
    year: t('Year'),
  };
  const groupLabel =
    groupLabelMap[groupBy as keyof typeof groupLabelMap] || groupBy;

  return (
    <Box>
      <Typography variant="body1" className={styles.sectionSubtitle}>
        {t('Grouped by %{group}', { group: groupLabel })}
      </Typography>
      {activeErgs.length > 0 && (
        <Box className={styles.chartLegend}>
          {activeErgs.map((ergType) => (
            <Box key={ergType} className={styles.chartLegendItem}>
              <Box
                className={styles.chartLegendSwatch}
                style={{ backgroundColor: ergTypeColor(ergType) }}
              />
              {ergTypeDisplay(ergType)}
            </Box>
          ))}
        </Box>
      )}
      <Box className={styles.chartScroll}>
        <Box className={styles.chartBody} style={{ minWidth: chartMinWidth }}>
          <Typography variant="caption" className={styles.chartYAxisLabel}>
            {humanizeDistance(maxTotal)}
          </Typography>
          <Box className={styles.chartPlotArea}>
            <Box className={styles.chartBaseline} />
            <Box className={styles.chartBars} style={{ marginRight: -barGap }}>
              {data.map((period) => {
                const stackHeight =
                  period.total > 0
                    ? Math.max((period.total / maxTotal) * CHART_PLOT_HEIGHT, 2)
                    : 0;

                return (
                  <Tooltip
                    key={period.period}
                    title={
                      <Box>
                        <Box>
                          {period.label}: {humanizeDistance(period.total)}
                        </Box>
                        {period.byErg
                          .filter((entry) => entry.total > 0)
                          .map((entry) => (
                            <Box key={entry.ergType}>
                              {ergTypeDisplay(entry.ergType as ErgType)}:{' '}
                              {humanizeDistance(entry.total)}
                            </Box>
                          ))}
                      </Box>
                    }
                    placement="bottom"
                    arrow
                  >
                    <Box
                      className={styles.chartBarColumn}
                      style={{ paddingRight: barGap }}
                    >
                      <Box
                        className={styles.chartBarStack}
                        style={{ height: stackHeight }}
                      >
                        {period.byErg
                          .filter((entry) => entry.total > 0)
                          .map((entry) => (
                            <Box
                              key={entry.ergType}
                              className={styles.chartBarSegment}
                              style={{
                                height:
                                  (entry.total / period.total) * stackHeight,
                                backgroundColor: ergTypeColor(
                                  entry.ergType as ErgType
                                ),
                              }}
                            />
                          ))}
                      </Box>
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          </Box>
          <Box className={styles.chartLabels} style={{ marginRight: -barGap }}>
            {data.map((period, index) => (
              <Box
                key={period.period}
                className={styles.chartLabelCell}
                style={{ paddingRight: barGap }}
              >
                {index % labelInterval === 0 ? period.label : ''}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const muiTheme = createMuiTheme({
  ...muiThemeAttributes,
  palette: {
    ...muiThemeAttributes.palette,
    type: 'dark',
    primary: {
      main: '#009e9a',
      light: '#00C9BE',
      dark: '#007a76',
    },
    secondary: {
      main: '#d9915e',
      light: '#e6a97a',
      dark: '#b3744a',
    },
    background: {
      default: '#1a1a1a',
      paper: '#2d2d2d',
    },
    text: {
      primary: '#ffffff',
      secondary: '#b0b0b0',
    },
  },
  typography: {
    ...muiThemeAttributes.typography,
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 500,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 500,
      lineHeight: 1.4,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 500,
      lineHeight: 1.4,
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    'none',
    '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
    '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',
    '0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)',
    '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
    '0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)',
    '0px 3px 5px -1px rgba(0,0,0,0.2),0px 6px 10px 0px rgba(0,0,0,0.14),0px 1px 18px 0px rgba(0,0,0,0.12)',
    '0px 4px 5px -2px rgba(0,0,0,0.2),0px 7px 10px 1px rgba(0,0,0,0.14),0px 2px 16px 1px rgba(0,0,0,0.12)',
    '0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)',
    '0px 5px 6px -3px rgba(0,0,0,0.2),0px 9px 12px 1px rgba(0,0,0,0.14),0px 3px 16px 2px rgba(0,0,0,0.12)',
    '0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)',
    '0px 6px 7px -4px rgba(0,0,0,0.2),0px 11px 15px 1px rgba(0,0,0,0.14),0px 4px 20px 3px rgba(0,0,0,0.12)',
    '0px 7px 8px -4px rgba(0,0,0,0.2),0px 12px 17px 2px rgba(0,0,0,0.14),0px 5px 22px 4px rgba(0,0,0,0.12)',
    '0px 7px 8px -4px rgba(0,0,0,0.2),0px 13px 19px 2px rgba(0,0,0,0.14),0px 5px 24px 4px rgba(0,0,0,0.12)',
    '0px 7px 9px -4px rgba(0,0,0,0.2),0px 14px 21px 2px rgba(0,0,0,0.14),0px 5px 26px 4px rgba(0,0,0,0.12)',
    '0px 8px 9px -5px rgba(0,0,0,0.2),0px 15px 22px 2px rgba(0,0,0,0.14),0px 6px 28px 5px rgba(0,0,0,0.12)',
    '0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)',
    '0px 8px 11px -5px rgba(0,0,0,0.2),0px 17px 26px 2px rgba(0,0,0,0.14),0px 6px 32px 5px rgba(0,0,0,0.12)',
    '0px 9px 11px -5px rgba(0,0,0,0.2),0px 18px 28px 2px rgba(0,0,0,0.14),0px 7px 34px 6px rgba(0,0,0,0.12)',
    '0px 9px 12px -6px rgba(0,0,0,0.2),0px 19px 29px 2px rgba(0,0,0,0.14),0px 7px 36px 6px rgba(0,0,0,0.12)',
    '0px 10px 13px -6px rgba(0,0,0,0.2),0px 20px 31px 3px rgba(0,0,0,0.14),0px 8px 38px 7px rgba(0,0,0,0.12)',
    '0px 10px 13px -6px rgba(0,0,0,0.2),0px 21px 33px 3px rgba(0,0,0,0.14),0px 8px 40px 7px rgba(0,0,0,0.12)',
    '0px 10px 14px -6px rgba(0,0,0,0.2),0px 22px 35px 3px rgba(0,0,0,0.14),0px 8px 42px 7px rgba(0,0,0,0.12)',
    '0px 11px 14px -7px rgba(0,0,0,0.2),0px 23px 36px 3px rgba(0,0,0,0.14),0px 9px 44px 8px rgba(0,0,0,0.12)',
    '0px 11px 15px -7px rgba(0,0,0,0.2),0px 24px 38px 3px rgba(0,0,0,0.14),0px 9px 46px 8px rgba(0,0,0,0.12)',
  ],
});

const defaultErgs = [
  ErgType.row,
  ErgType.ski,
  ErgType.bike,
  ErgType.fanBike,
  ErgType.run,
  ErgType.multi,
];

const StatItem = ({
  name,
  children,
  color = 'primary',
  style,
  previousValue,
  newValue,
}: {
  name: string;
  children: ReactNode;
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error';
  style?: any;
  previousValue?: number;
  newValue?: number;
}) => {
  const styles = useStyles();

  const getColor = () => {
    switch (color) {
      case 'primary':
        return '#009e9a';
      case 'secondary':
        return '#d9915e';
      case 'success':
        return '#4caf50';
      case 'warning':
        return '#ff9800';
      case 'error':
        return '#f44336';
      default:
        return '#009e9a';
    }
  };

  const percentage = calculateProgressFromPrevious(previousValue, newValue);
  const isIncrease = percentage > 0;

  return (
    <Card className={styles.statCard} style={style} elevation={3}>
      <CardContent className={styles.statCardContent}>
        <Typography variant="h6" className={styles.statTitle}>
          {name}
        </Typography>
        {!!percentage && (
          <Tooltip
            title={
              isIncrease
                ? t(
                    'Increase of %{percentage}% compared to the previous period',
                    { percentage: Math.round(Math.abs(percentage)) }
                  )
                : t(
                    'Decrease of %{percentage}% compared to the previous period',
                    { percentage: Math.round(Math.abs(percentage)) }
                  )
            }
            placement="top"
            arrow
          >
            <Box className={styles.statPercentageHeader}>
              <MaterialIcon
                name={isIncrease ? 'arrow-upward' : 'arrow-downward'}
                size={18}
                color={isIncrease ? green : red}
              />
              {Math.round(Math.abs(percentage))}
              <span style={{ fontSize: 14 }}>%</span>
            </Box>
          </Tooltip>
        )}
        <Typography
          variant="h4"
          className={styles.statValue}
          style={{ color: getColor() }}
        >
          {children}
        </Typography>
      </CardContent>
    </Card>
  );
};

const humanizeTime = (value: number) => {
  const duration = moment.duration(value * 1000);
  const years = duration.years();
  const months = duration.months();
  const days = duration.days();
  const hours = duration.hours();
  const minutes = duration.minutes();

  const values = [];

  if (years) {
    values.push(`${years}y`);
    values.push(`${months}m`);
    values.push(`${days}d`);
  } else if (months) {
    values.push(`${months}m`);
    values.push(`${days}d`);
  } else if (days) {
    values.push(`${days}d`);
    if (hours) values.push(`${hours}h`);
  } else {
    if (hours) values.push(`${hours}h`);
    if (minutes) values.push(`${minutes}m`);
  }

  return values.join(' ');
};

const ERG_RATES = [ErgType.row, ErgType.ski, ErgType.bike, ErgType.fanBike];
const displayRates = (rates: number[]) => {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
      {rates.map((rate, index) => {
        return rate ? (
          <div
            key={index}
            style={{
              border: '1px solid rgba(255,255,255, 0.1)',
              borderRadius: 5,
              padding: 4,
              paddingBottom: 2,
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              marginRight: index === rates.length - 1 ? 0 : 10,
              fontSize: '0.66em',
            }}
          >
            <ErgIcon
              style={{ marginTop: -10 }}
              ergType={ERG_RATES[index]}
              size={15}
              color="white"
            />
            {Math.round(rate)}
          </div>
        ) : null;
      })}
    </div>
  );
};

const getYears = (insertedAt: string) => {
  const start = moment(insertedAt).year();
  const end = moment().year();
  return Array.from(Array(end - start + 1).keys())
    .map((i) => start + i)
    .reverse();
};

const UserStatsPage = () => {
  const styles = useStyles();
  const [time, setTime] = useState('30-day');
  const [ergTypes, setErgTypes] = useState<ErgType[]>([]);
  const [customDateRange, setCustomDateRange] = useState<DateRange>();
  const [showDateRangeModal, setShowDateRangeModal] = useState(false);
  const { currentUser } = useCurrentUser();

  const variables = {
    time,
    ...(time === 'custom' ? customDateRange : {}),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    skipPreviousStats: time === 'lifetime',
    ergTypes: ergTypes.length === 0 ? defaultErgs : ergTypes,
  };

  const { data, loading } = useQuery<QueryData>(GET_STATS, {
    fetchPolicy: 'network-only',
    variables,
  });

  useEffect(() => {
    document.body.style.backgroundColor = bgColor;
    setTimeout(() => postMobileMessage('loaded'), 100);
  }, []);

  const stats = data ? data.userStats.recentStats : [];
  const distanceOverTime = data?.userStats.distanceOverTime || [];
  const distanceGroupBy = data?.userStats.distanceGroupBy || 'day';
  const hasDistanceChartData = distanceOverTime.some(
    (period) => period.total > 0
  );
  const currentStats = buildStats(data ? data.userStats : undefined);
  const previousStats = buildStats(data ? data.previousStats : undefined);

  const firstMetric = stats[0] ? stats[0].total : 1;
  const years = getYears(currentUser.insertedAt);

  const history = useHistory();

  const isAllErgTypes =
    ergTypes.length === 0 || ergTypes.length === defaultErgs.length;
  const chartErgs = isAllErgTypes ? defaultErgs : ergTypes;

  const LoadingSkeleton = () => (
    <Box className={styles.skeletonContainer}>
      <Grid container spacing={3}>
        {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
          <Grid item xs={6} sm={6} md={3} key={i}>
            <Card className={styles.statCard} elevation={3}>
              <CardContent>
                <Box
                  style={{
                    height: 24,
                    width: '60%',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    marginBottom: 8,
                    borderRadius: 4,
                  }}
                />
                <Box
                  style={{
                    height: 32,
                    width: '80%',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    borderRadius: 4,
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  return (
    <>
      {loading && (
        <ProgressBar
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
          }}
          indeterminate
        />
      )}
      <Box className={styles.container}>
        <Fade in={!!data} timeout={500}>
          <Box className={styles.content}>
            {/* Header Section */}
            <Card className={styles.headerCard} elevation={4}>
              <CardContent className={styles.headerContent}>
                <Box className={styles.headerTop}>
                  {!isMobileApp() && (
                    <Box className={styles.backButton}>
                      <IconButton
                        onClick={() => history.goBack()}
                        className={styles.backIconButton}
                      >
                        <MaterialIcon
                          name="arrow-back"
                          color="white"
                          size={24}
                        />
                      </IconButton>
                    </Box>
                  )}

                  <Box className={styles.filtersContainer}>
                    <Box className={styles.ergFilters}>
                      <FormControl
                        variant="outlined"
                        className={styles.ergFormControl}
                      >
                        <InputLabel className={styles.ergLabel}>
                          {t('Erg Types')}
                        </InputLabel>
                        <Select
                          multiple
                          value={isAllErgTypes ? ['all'] : ergTypes}
                          onChange={(event) => {
                            const value = (
                              event.target.value as string[]
                            ).filter((v) => v !== 'all');
                            setErgTypes(value as ErgType[]);
                          }}
                          label={t('Erg Types')}
                          className={styles.ergSelect}
                          renderValue={(selected: any) => {
                            const selectedValues = selected as string[];

                            if (isAllErgTypes) {
                              return (
                                <Typography
                                  variant="body2"
                                  style={{ marginLeft: 8 }}
                                >
                                  {t('All')}
                                </Typography>
                              );
                            }

                            return (
                              <Box className={styles.chipContainer}>
                                {selectedValues.map((value) => (
                                  <Chip
                                    key={value}
                                    label={
                                      <Box className={styles.chipContent}>
                                        <ErgIcon
                                          ergType={value as ErgType}
                                          size={14}
                                          color="#ffffff"
                                        />
                                        <span style={{ marginLeft: 4 }}>
                                          {ergTypeDisplay(value as ErgType)}
                                        </span>
                                      </Box>
                                    }
                                    className={styles.chip}
                                    size="small"
                                  />
                                ))}
                              </Box>
                            );
                          }}
                        >
                          {defaultErgs.map((ergType) => (
                            <MenuItem key={ergType} value={ergType}>
                              <Box className={styles.menuItemContent}>
                                <ErgIcon
                                  ergType={ergType}
                                  size={16}
                                  color="#ffffff"
                                />
                                <Typography
                                  variant="body2"
                                  style={{ marginLeft: 8 }}
                                >
                                  {ergTypeDisplay(ergType)}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    <Box className={styles.timeframeSelector}>
                      <FormControl
                        variant="outlined"
                        className={styles.timeframeFormControl}
                      >
                        <InputLabel className={styles.timeframeLabel}>
                          {t('Time Period')}
                        </InputLabel>
                        <Select
                          labelId="timeframe-label"
                          value={time}
                          onChange={(event) => {
                            if (event.target.value !== 'custom') {
                              setTime(event.target.value as string);
                            }
                          }}
                          label={t('Time Period')}
                          className={styles.timeframeSelect}
                        >
                          <MenuItem value="1-day">{t('Last 1 day')}</MenuItem>
                          <MenuItem value="7-day">{t('Last 7 days')}</MenuItem>
                          <MenuItem value="30-day">
                            {t('Last 30 days')}
                          </MenuItem>
                          <MenuItem value="365-day">
                            {t('Last 365 days')}
                          </MenuItem>
                          {years.map((year) => (
                            <MenuItem key={year} value={`year-${year}`}>
                              {year}
                            </MenuItem>
                          ))}
                          <MenuItem value="lifetime">{t('Lifetime')}</MenuItem>
                          <MenuItem
                            value="custom"
                            onClick={() => setShowDateRangeModal(true)}
                          >
                            {customDateRangeDisplay(customDateRange)}
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>

                  {!isMobileApp() && (
                    <Box className={styles.poweredBy}>
                      <Typography
                        variant="body2"
                        className={styles.poweredByText}
                      >
                        Powered by
                      </Typography>
                      <Logo size={24} darkMode />
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>

            {/* Stats Grid */}
            {loading ? (
              <LoadingSkeleton />
            ) : (
              <Box className={styles.statsSection}>
                <Typography variant="h4" className={styles.sectionTitle}>
                  {t('Overall Stats')}
                </Typography>
                <Grid container spacing={3} className={styles.statsGrid}>
                  <Grid item xs={6} sm={6} md={3}>
                    <StatItem
                      name={t('Total Meters')}
                      color="primary"
                      previousValue={previousStats.totalDistance}
                      newValue={currentStats.totalDistance}
                    >
                      {currentStats.totalDistance
                        ? humanizeDistance(currentStats.totalDistance)
                        : '-'}
                    </StatItem>
                  </Grid>
                  <Grid item xs={6} sm={6} md={3}>
                    <StatItem
                      name={t('Total Time')}
                      color="secondary"
                      previousValue={previousStats.totalTime}
                      newValue={currentStats.totalTime}
                    >
                      {currentStats.totalTime
                        ? humanizeTime(currentStats.totalTime)
                        : '-'}
                    </StatItem>
                  </Grid>
                  <Grid item xs={6} sm={6} md={3}>
                    <StatItem
                      name={t('Active Days')}
                      color="success"
                      previousValue={previousStats.totalDays}
                      newValue={currentStats.totalDays}
                    >
                      {currentStats.totalDays ? currentStats.totalDays : '-'}
                    </StatItem>
                  </Grid>
                  <Grid item xs={6} sm={6} md={3}>
                    <StatItem
                      name={t('Total Calories')}
                      color="warning"
                      previousValue={previousStats.totalCalories}
                      newValue={currentStats.totalCalories}
                    >
                      {currentStats.totalCalories
                        ? humanizeDistance(currentStats.totalCalories, {
                            meters: '',
                          })
                        : '-'}
                    </StatItem>
                  </Grid>
                  <Grid item xs={6} sm={6} md={3}>
                    <StatItem
                      name={t('Average Pace')}
                      color="primary"
                      previousValue={
                        previousStats.pace && calculatePower(previousStats.pace)
                      }
                      newValue={
                        currentStats.pace && calculatePower(currentStats.pace)
                      }
                    >
                      {currentStats.pace
                        ? formatTime(currentStats.pace, true)
                        : '-'}
                    </StatItem>
                  </Grid>
                  <Grid item xs={6} sm={6} md={3}>
                    <StatItem
                      name={t('Average Watts')}
                      color="secondary"
                      previousValue={
                        previousStats.pace && calculatePower(previousStats.pace)
                      }
                      newValue={
                        currentStats.pace && calculatePower(currentStats.pace)
                      }
                    >
                      {currentStats.pace
                        ? calculatePower(currentStats.pace).toFixed(0)
                        : '-'}
                    </StatItem>
                  </Grid>
                  <Grid item xs={6} sm={6} md={3}>
                    <StatItem name={t('Average Rate')} color="success">
                      {currentStats.rates.reduce((a, b) => a + b, 0) > 0
                        ? displayRates(currentStats.rates)
                        : '-'}
                    </StatItem>
                  </Grid>
                  <Grid item xs={6} sm={6} md={3}>
                    <StatItem name={t('Heart Rate Zones')} color="error">
                      {currentStats.hrZones.reduce((a, b) => a + b, 0) > 0 ? (
                        <Box className={styles.hrZonesContainer}>
                          <HRZones
                            currentZone={currentStats.hrZones.indexOf(
                              Math.max(...currentStats.hrZones)
                            )}
                            fontSize={20}
                            hrZones={currentStats.hrZones}
                            theme={darkTheme}
                          />
                        </Box>
                      ) : (
                        '-'
                      )}
                    </StatItem>
                  </Grid>
                </Grid>
              </Box>
            )}

            {!loading && hasDistanceChartData && (
              <Box className={styles.distanceChartSection}>
                <Typography variant="h4" className={styles.sectionTitle}>
                  {t('Meters Over Time')}
                </Typography>
                <Card className={styles.chartCard} elevation={3}>
                  <CardContent>
                    <DistanceOverTimeChart
                      data={distanceOverTime}
                      groupBy={distanceGroupBy}
                      ergTypes={chartErgs}
                    />
                  </CardContent>
                </Card>
              </Box>
            )}

            {/* Recent Stats Section */}
            {!loading && stats.length > 0 && (
              <Box className={styles.recentStatsSection}>
                <Typography variant="h4" className={styles.sectionTitle}>
                  {t('Recent Activity')}
                </Typography>
                <Typography variant="body1" className={styles.sectionSubtitle}>
                  {t('Your recent distances by group')}
                </Typography>
                <Box className={styles.rowsContainer}>
                  {stats.map((stat) => (
                    <Row
                      key={stat.name}
                      stat={stat}
                      percentage={stat.total / firstMetric}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Fade>
      </Box>
      {showDateRangeModal && (
        <DateRangeModal
          minDate={moment().subtract(100, 'years')}
          maxDate={moment()}
          initialDateRange={
            customDateRange || {
              startDate: moment().subtract('7', 'days').format('YYYY-MM-DD'),
              endDate: moment().format('YYYY-MM-DD'),
            }
          }
          onDismiss={() => {
            setShowDateRangeModal(false);
          }}
          onComplete={(range) => {
            setTime('custom');
            setShowDateRangeModal(false);
            setCustomDateRange(range);
          }}
        />
      )}
    </>
  );
};

const bgColor = '#1a1a1a';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${bgColor} 0%, #2d2d2d 100%)`,
      padding: theme.spacing(3),
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2),
      },
    },
    content: {
      maxWidth: 1400,
      margin: '0 auto',
    },
    headerCard: {
      background: 'linear-gradient(135deg, #2d2d2d 0%, #3d3d3d 100%)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      marginBottom: theme.spacing(4),
    },
    headerContent: {
      padding: theme.spacing(3),
    },
    headerTop: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: `${theme.spacing(2)}px`,
      [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
        alignItems: 'stretch',
      },
    },
    backButton: {
      display: 'flex',
      alignItems: 'center',
      gap: `${theme.spacing(2)}px`,
    },
    backIconButton: {
      color: theme.palette.text.primary,
    },
    filtersContainer: {
      flex: 1,
      minWidth: 200,
      display: 'flex',
      gap: `${theme.spacing(3)}px`,
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        gap: `${theme.spacing(2)}px`,
      },
    },
    timeframeSelector: {
      flex: 1,
      minWidth: 200,
    },
    timeframeFormControl: {
      width: '100%',
    },
    timeframeLabel: {
      color: theme.palette.text.secondary,
    },
    timeframeSelect: {
      color: theme.palette.text.primary,
      '& .MuiSelect-icon': {
        color: theme.palette.text.secondary,
      },
    },
    poweredBy: {
      display: 'flex',
      alignItems: 'center',
      gap: `${theme.spacing(1)}px`,
    },
    poweredByText: {
      color: theme.palette.text.secondary,
    },
    ergFilters: {
      flex: 1,
      minWidth: 200,
    },
    filterTitle: {
      color: theme.palette.text.primary,
      marginBottom: theme.spacing(2),
    },
    ergFormControl: {
      width: '100%',
    },
    ergLabel: {
      color: theme.palette.text.secondary,
    },
    ergSelect: {
      color: theme.palette.text.primary,
      '& .MuiSelect-icon': {
        color: theme.palette.text.secondary,
      },
    },
    chipContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: `${theme.spacing(0.5)}px`,
    },
    chipContent: {
      display: 'flex',
      alignItems: 'center',
    },
    chip: {
      backgroundColor: 'rgba(255,255,255,0.1)',
      borderRadius: 12,
      padding: theme.spacing(0.5),
    },
    menuItemContent: {
      display: 'flex',
      alignItems: 'center',
    },
    skeletonContainer: {
      marginTop: theme.spacing(4),
    },
    statsSection: {
      marginBottom: theme.spacing(6),
    },
    sectionTitle: {
      color: theme.palette.text.primary,
      marginBottom: `${theme.spacing(3)}px`,
      fontWeight: 600,
    },
    sectionSubtitle: {
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(4),
    },
    statsGrid: {
      marginBottom: theme.spacing(4),
    },
    statCard: {
      background: 'linear-gradient(135deg, #2d2d2d 0%, #3d3d3d 100%)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      transition: 'all 0.3s ease',
      height: '100%',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: theme.shadows[8],
        border: '1px solid rgba(255, 255, 255, 0.2)',
      },
    },
    statCardContent: {
      padding: theme.spacing(3),
      minHeight: 60,
      overflow: 'visible',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      position: 'relative',
    },
    statTitle: {
      color: theme.palette.text.secondary,
      fontWeight: 500,
    },
    statPercentageHeader: {
      display: 'flex',
      alignItems: 'center',
      position: 'absolute',
      top: theme.spacing(2),
      right: theme.spacing(2),
      padding: `${theme.spacing(0.5)}px ${theme.spacing(1)}px`,
      borderRadius: theme.shape.borderRadius,
      background: 'rgba(255, 255, 255, 0.1)',
      fontSize: '0.875rem',
      fontWeight: 500,
      [theme.breakpoints.down('sm')]: {
        top: theme.spacing(1),
        right: theme.spacing(1),
        padding: `${theme.spacing(0.25)}px ${theme.spacing(0.5)}px`,
        fontSize: '0.75rem',
        zIndex: 1,
      },
    },
    statValue: {
      fontWeight: 700,
      fontSize: '1.5rem',
      wordBreak: 'break-word',
      marginTop: theme.spacing(1),
      [theme.breakpoints.up('md')]: {
        fontSize: '2rem',
      },
      [theme.breakpoints.up('lg')]: {
        fontSize: '2.25rem',
      },
    },
    hrZonesContainer: {
      display: 'flex',
      justifyContent: 'flex-start',
      transform: 'scale(1.2)',
      marginLeft: theme.spacing(2),
    },
    recentStatsSection: {
      marginTop: theme.spacing(6),
    },
    distanceChartSection: {
      marginBottom: theme.spacing(6),
    },
    chartCard: {
      background: 'linear-gradient(135deg, #2d2d2d 0%, #3d3d3d 100%)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
    },
    chartScroll: {
      overflowX: 'auto',
      paddingBottom: theme.spacing(1),
    },
    chartBody: {
      position: 'relative',
      width: '100%',
    },
    chartYAxisLabel: {
      position: 'absolute',
      left: 0,
      top: 0,
      zIndex: 1,
      color: theme.palette.text.secondary,
      fontSize: '0.7rem',
      lineHeight: 1,
      pointerEvents: 'none',
    },
    chartPlotArea: {
      position: 'relative',
      height: CHART_PLOT_HEIGHT,
      borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
    },
    chartBaseline: {
      position: 'absolute',
      left: 0,
      right: 0,
      top: 0,
      borderTop: '1px dashed rgba(255, 255, 255, 0.08)',
    },
    chartBars: {
      display: 'flex',
      alignItems: 'flex-end',
      height: '100%',
      width: '100%',
    },
    chartBarColumn: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
      flex: '1 1 0',
      minWidth: 8,
      height: '100%',
      boxSizing: 'border-box',
      cursor: 'pointer',
    },
    chartBarStack: {
      display: 'flex',
      flexDirection: 'column-reverse',
      width: '100%',
      borderRadius: '3px 3px 0 0',
      overflow: 'hidden',
    },
    chartBarSegment: {
      width: '100%',
      flexShrink: 0,
    },
    chartLabels: {
      display: 'flex',
      width: '100%',
      marginTop: theme.spacing(1),
    },
    chartLabelCell: {
      flex: '1 1 0',
      minWidth: 8,
      boxSizing: 'border-box',
      color: theme.palette.text.secondary,
      fontSize: '0.65rem',
      textAlign: 'center',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      minHeight: 16,
    },
    chartLegend: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: `${theme.spacing(2)}px`,
      marginBottom: theme.spacing(3),
    },
    chartLegendItem: {
      display: 'flex',
      alignItems: 'center',
      gap: `${theme.spacing(0.75)}px`,
      color: theme.palette.text.secondary,
      fontSize: '0.875rem',
    },
    chartLegendSwatch: {
      width: 12,
      height: 12,
      borderRadius: 2,
    },
    rowsContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: `${theme.spacing(3)}px`,
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
      },
      paddingBottom: theme.spacing(3),
    },
    row: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(1),
      background: 'linear-gradient(135deg, #2d2d2d 0%, #3d3d3d 100%)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: theme.shape.borderRadius,
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[4],
        border: '1px solid rgba(255, 255, 255, 0.2)',
      },
    },
    bar: {
      display: 'flex',
      flexShrink: 0,
      padding: theme.spacing(1),
      borderRadius: theme.shape.borderRadius,
      minWidth: theme.spacing(6),
      justifyContent: 'flex-end',
      transition: 'width 0.4s',
      boxShadow:
        'rgba(0, 0, 0, 0.1) 0px 0px 5px 0px, rgba(0, 0, 0, 0.1) 0px 0px 1px 0px',
    },
    image: {
      width: theme.spacing(4),
      height: theme.spacing(4),
      objectFit: 'cover',
      borderRadius: '50%',
      boxShadow:
        'rgba(0, 0, 0, 0.1) 0px 0px 5px 0px, rgba(0, 0, 0, 0.1) 0px 0px 1px 0px',
    },
    rowMetricContainer: {
      color: theme.palette.text.primary,
      minWidth: 0,
      marginLeft: theme.spacing(2),
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    },
    rowName: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
  })
);

function buildStats(userStats?: UserStats) {
  const totalDistance = userStats ? userStats.totalDistance : 0;
  const totalDays = userStats ? userStats.totalDays : 0;
  const totalTime = userStats ? userStats.totalTime : 0;
  const totalCalories = userStats ? userStats.totalCalories : 0;
  const hrZones = userStats ? userStats.hrZones : [];
  const rates = userStats ? userStats.rates : [];
  const pace = userStats
    ? calculatePace(
        userStats.totalTime,
        userStats.weightedDistance,
        ErgType.row
      )
    : 0;
  return {
    totalDistance,
    totalTime,
    totalDays,
    totalCalories,
    pace,
    rates,
    hrZones,
  };
}

export default function UserStatsPageWrapper() {
  return (
    <ThemeProvider theme={muiTheme}>
      <UserStatsPage />
    </ThemeProvider>
  );
}
